import { useCallback, useEffect, useMemo, useState } from "react";
import { TrashSimple } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  DataCell,
  DataRow,
  DataTable,
  SearchField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@breakingthelines/design-system";

import { LoadingSpinner } from "@/components/common/loading-spinner";
import {
  deleteEntityAssetByKey,
  listEntityAssets,
  listEntityOverrides,
  type EntityAssetListing,
  type EntityOverride,
} from "@/lib/admin-api";
import {
  ALL_ENTITY_CHANGES,
  ENTITY_CHANGE_FACETS,
  entityAssetRoleSpec,
  entityChangeFacetLabel,
  entityChangeRole,
  entityChangeSources,
  entityKindForId,
  entityRowMatchesQuery,
  formatBytes,
  formatDateTime,
  overrideChangeLabels,
  overrideMatchesChangeFacet,
  type EntityAssetRole,
  type EntityChangeFacet,
} from "@/lib/entity-assets";

/**
 * The Corrections tab: everything an admin has changed across the estate, text
 * and artwork in one place. Entity assets had three subtabs (#85) and the owner
 * asked for two - the Entity tab, which edits one entity, and this, which lists
 * what has been edited. The old /entities/assets/artwork path redirects here
 * (see legacy-redirect.tsx and router.tsx).
 *
 * # WHY THIS IS TWO SECTIONS AND NOT ONE MERGED STREAM
 *
 * The two sources page incompatibly. Text corrections come from game-service on
 * a DB cursor, most recently edited first. Artwork comes from content-service on
 * a bucket prefix, in lexicographic key order. Interleaving them by date would
 * mean holding both cursors and guessing which to spend next, and the merged
 * order would be a lie the moment either list had a second page. So the sources
 * keep their own cursors and their own Load more, and the facet decides which
 * of them feeds the list:
 *
 *   All changes      both, under a "Text" and an "Artwork" heading
 *   Name, Biography  the text list only
 *   Crest ... Stadium  the artwork list only, narrowed BY THE SERVER
 *
 * The list is one list in the way that matters: one search box and one facet
 * over both, and every row says what changed, on which entity, and when.
 *
 * # WHAT THE SEARCH BOX CAN AND CANNOT SEE
 *
 * Neither source has a search endpoint, so the box narrows the rows already
 * loaded. That is honest as long as the page says so, which it does whenever a
 * narrowing is client-side AND a cursor is still live. An image facet is not
 * one of those cases: it is sent as a role and the server does the narrowing,
 * so paging through Crest pages through every crest in the store.
 *
 * Row activation opens the entity on the Entity tab, the same
 * `?id=<entityId>` contract the Artwork tab used (entity-editor.tsx reads it
 * back out). Rows whose id is outside the five kinds this surface edits have
 * nowhere to go, so they do not activate.
 *
 * # WHY AN ARTWORK ROW CAN BE REMOVED HERE AND NOWHERE ELSE
 *
 * The listing reports MIS-FILED objects on purpose, and a mis-filed object is
 * unreachable from the Entity tab: that tab addresses (role, entityId), and the
 * rows in question are the ones whose id and directory disagree, so the pair it
 * would send is refused. The row is the only place the object is visible, so it
 * is where the removal belongs, and it goes by the key the listing gave rather
 * than by anything rebuilt from the row (content-service#225).
 */

const TEXT_PAGE_SIZE = 100;
const ARTWORK_PAGE_SIZE = 100;

export function EntityCorrectionsPage() {
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [facet, setFacet] = useState<EntityChangeFacet>(ALL_ENTITY_CHANGES);

  const [overrides, setOverrides] = useState<EntityOverride[]>([]);
  const [overridesToken, setOverridesToken] = useState("");
  const [overridesLoading, setOverridesLoading] = useState(true);
  const [overridesError, setOverridesError] = useState<string | null>(null);

  const [assets, setAssets] = useState<EntityAssetListing[]>([]);
  const [assetsToken, setAssetsToken] = useState("");
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [assetsError, setAssetsError] = useState<string | null>(null);

  // A removal in flight, and its outcome. Separate from the listing's own
  // loading and error, because a refused removal must not blank the list or be
  // read as the listing having failed.
  const [removing, setRemoving] = useState(false);
  const [removeStatus, setRemoveStatus] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const sources = entityChangeSources(facet);

  const loadOverrides = useCallback(async (pageToken?: string) => {
    setOverridesLoading(true);
    setOverridesError(null);
    try {
      const response = await listEntityOverrides({ pageSize: TEXT_PAGE_SIZE, pageToken });
      setOverrides((prev) => (pageToken ? [...prev, ...response.overrides] : response.overrides));
      setOverridesToken(response.nextPageToken ?? "");
    } catch (err) {
      setOverridesError(err instanceof Error ? err.message : "Could not load corrections.");
    } finally {
      setOverridesLoading(false);
    }
  }, []);

  const loadAssets = useCallback(async (role?: EntityAssetRole, pageToken?: string) => {
    setAssetsLoading(true);
    setAssetsError(null);
    try {
      const response = await listEntityAssets({ pageSize: ARTWORK_PAGE_SIZE, pageToken, role });
      setAssets((prev) => (pageToken ? [...prev, ...response.assets] : response.assets));
      // The token, not the row count, says whether there is more.
      // content-service skips keys that do not parse as an entity asset after
      // the object store has already spent them against the page, so a page can
      // come back empty and still have somewhere to go.
      setAssetsToken(response.nextPageToken ?? "");
    } catch (err) {
      setAssetsError(err instanceof Error ? err.message : "Could not load artwork.");
    } finally {
      setAssetsLoading(false);
    }
  }, []);

  // Once per visit, and kept. The facet cannot narrow this list server-side, so
  // re-requesting it on a facet change would throw away pages the operator has
  // already loaded and is searching.
  useEffect(() => {
    void loadOverrides();
  }, [loadOverrides]);

  // The artwork list IS narrowed server-side, so it is re-requested whenever
  // the facet changes the role, and skipped while a text facet is showing.
  useEffect(() => {
    if (!entityChangeSources(facet).artwork) return;
    void loadAssets(entityChangeRole(facet));
  }, [facet, loadAssets]);

  function chooseFacet(next: EntityChangeFacet) {
    // A new facet is a new artwork request, so the bucket cursor goes with it.
    // The text cursor stays: nothing about it changes with the facet.
    setAssets([]);
    setAssetsToken("");
    setRemoveStatus(null);
    setRemoveError(null);
    setFacet(next);
  }

  /**
   * Remove one stored object, from the row that reports it.
   *
   * THE KEY IS THE ROW'S, SENT VERBATIM. A row here can be MIS-FILED - a coach
   * id sitting in the players-only avatar directory - and that is the case this
   * action exists for: the object never resolves, the entity page shows a
   * monogram forever, and this listing is the only place it surfaces. The
   * (role, entityId) route cannot address it, because the id fails the role's
   * prefix check. Rebuilding the key from the row's role and id would rebuild
   * the same disagreement and remove nothing, or the wrong object.
   *
   * The row leaves the list on either outcome. An empty `deletedKeys` means the
   * object was already gone, which makes the row stale rather than the removal
   * failed. Dropping it locally rather than re-listing is what the rest of this
   * page does with its cursors: a refetch would start the bucket cursor again
   * and throw away every page the operator has loaded.
   */
  async function removeAsset(asset: EntityAssetListing) {
    const roleSpec = entityAssetRoleSpec(asset.role);
    const confirmed = window.confirm(
      `Remove the ${roleSpec.label.toLowerCase()} for ${asset.entityId}? The provider image shows instead, or a monogram where there is none.`,
    );
    if (!confirmed) return;

    setRemoveStatus(null);
    setRemoveError(null);
    setRemoving(true);
    try {
      const response = await deleteEntityAssetByKey(asset.objectKey);
      setAssets((prev) => prev.filter((row) => row.objectKey !== asset.objectKey));
      setRemoveStatus(
        response.deletedKeys.length > 0
          ? `${roleSpec.label} removed.`
          : `There was no ${roleSpec.label.toLowerCase()} to remove.`,
      );
    } catch (err) {
      // The server refused the key, or the request never landed. Either way the
      // object is still there, so the row stays.
      setRemoveError(err instanceof Error ? err.message : "Could not remove the artwork.");
    } finally {
      setRemoving(false);
    }
  }

  /** Open a row's entity on the Entity tab. The id says which kind it is. */
  const loadEntity = useCallback(
    (entityId: string) => {
      navigate(`/entities/assets/entity?id=${encodeURIComponent(entityId)}`);
    },
    [navigate],
  );

  const shownOverrides = useMemo(
    () =>
      sources.text
        ? overrides.filter(
            (override) =>
              overrideMatchesChangeFacet(override, facet) && entityRowMatchesQuery(override, query),
          )
        : [],
    [overrides, facet, query, sources.text],
  );

  const shownAssets = useMemo(
    () => (sources.artwork ? assets.filter((asset) => entityRowMatchesQuery(asset, query)) : []),
    [assets, query, sources.artwork],
  );

  // What the filter searched, and whether that was everything there is.
  const searchedRows =
    (sources.text ? overrides.length : 0) + (sources.artwork ? assets.length : 0);
  const unloadedPagesRemain =
    (sources.text && overridesToken !== "") || (sources.artwork && assetsToken !== "");
  // Only a client-side narrowing needs saying. An image facet is sent as a role
  // and narrowed by the server, so it is complete however many pages are left.
  const narrowedOverLoadedRows = query.trim() !== "" || facet === "name" || facet === "biography";

  return (
    <div className="flex w-full min-w-0 flex-col gap-5 pb-16">
      <section className="glass-card rounded-btl-md flex min-w-0 flex-col gap-5 p-5">
        <div className="min-w-0">
          <h2 className="m-0 text-[15px] font-semibold tracking-tight text-white">Corrections</h2>
          <p className="text-muted-foreground m-0 mt-1 text-xs leading-relaxed">
            Every name, biography and image an admin has changed.
          </p>
        </div>

        <div className="flex min-w-0 flex-col gap-4 md:flex-row md:items-end">
          <div className="flex min-w-0 flex-col gap-2 md:max-w-[320px] md:flex-1">
            <label className="text-xs font-semibold text-white" htmlFor="corrections-search">
              Search
            </label>
            <SearchField
              id="corrections-search"
              label="Search corrections"
              placeholder="Entity id or name"
              autoComplete="off"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
            />
          </div>

          <div className="flex min-w-0 flex-col gap-2 md:max-w-[280px] md:flex-1">
            <span className="text-xs font-semibold text-white">Changed</span>
            <Select
              value={facet}
              onValueChange={(value: string) => chooseFacet(value as EntityChangeFacet)}
            >
              <SelectTrigger aria-label="Changed">
                <SelectValue>
                  {(value: string | null) =>
                    entityChangeFacetLabel((value ?? ALL_ENTITY_CHANGES) as EntityChangeFacet)
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ENTITY_CHANGES}>All changes</SelectItem>
                {ENTITY_CHANGE_FACETS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {narrowedOverLoadedRows && unloadedPagesRemain ? (
          <p className="text-muted-foreground m-0 text-xs leading-relaxed">
            Filtered from {searchedRows} loaded row{searchedRows === 1 ? "" : "s"}. More rows exist.
          </p>
        ) : null}

        {sources.text ? (
          <TextCorrections
            rows={shownOverrides}
            loadedCount={overrides.length}
            heading={sources.artwork}
            loading={overridesLoading}
            error={overridesError}
            nextPageToken={overridesToken}
            onLoadMore={() => void loadOverrides(overridesToken)}
            onLoad={loadEntity}
          />
        ) : null}

        {sources.artwork ? (
          <ArtworkChanges
            rows={shownAssets}
            loadedCount={assets.length}
            heading={sources.text}
            role={entityChangeRole(facet)}
            loading={assetsLoading}
            error={assetsError}
            nextPageToken={assetsToken}
            onLoadMore={() => void loadAssets(entityChangeRole(facet), assetsToken)}
            onLoad={loadEntity}
            onRemove={(asset) => void removeAsset(asset)}
            removing={removing}
            removeStatus={removeStatus}
            removeError={removeError}
          />
        ) : null}
      </section>
    </div>
  );
}

/**
 * The row's own way in, kept as a real button beside the row's click target.
 *
 * DataRow's `onActivate` is a pointer convenience and deliberately leaves the
 * row a `role="row"`, so the button is what a screen reader announces as the
 * path to the entity. Named for the row it belongs to: the picker on the
 * Entity tab has its own Load, and two controls reading only "Load" are
 * ambiguous to a screen reader and to a test.
 */
function RowLoadButton({
  entityId,
  onLoad,
}: {
  entityId: string;
  onLoad: (entityId: string) => void;
}) {
  return (
    <Button
      variant="outline"
      aria-label={`Load ${entityId}`}
      disabled={entityKindForId(entityId) === null}
      onClick={(event) => {
        // The row activates on click too. Without this the button's own click
        // bubbles into it and the same navigation happens twice.
        event.stopPropagation();
        onLoad(entityId);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") event.stopPropagation();
      }}
    >
      Load
    </Button>
  );
}

/**
 * Remove the object this row reports.
 *
 * Named for its row like the Load beside it, and named with the role too: one
 * entity can hold two rows (a player has an avatar and a hero), so "Remove
 * btl_football_player_9" would be two controls under one name.
 */
function RowRemoveButton({
  asset,
  disabled,
  onRemove,
}: {
  asset: EntityAssetListing;
  disabled: boolean;
  onRemove: (asset: EntityAssetListing) => void;
}) {
  const roleSpec = entityAssetRoleSpec(asset.role);
  return (
    <Button
      variant="outline"
      aria-label={`Remove ${roleSpec.label.toLowerCase()} for ${asset.entityId}`}
      disabled={disabled}
      onClick={(event) => {
        // The row activates on click and would navigate away from the confirm.
        event.stopPropagation();
        onRemove(asset);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") event.stopPropagation();
      }}
    >
      <TrashSimple size={14} weight="bold" /> Remove
    </Button>
  );
}

function SectionHeading({ children }: { children: string }) {
  return (
    <h3 className="text-muted-foreground m-0 text-[11px] font-semibold tracking-wider uppercase">
      {children}
    </h3>
  );
}

/**
 * What to say when a section shows nothing, or null to say nothing at all.
 *
 * Three different silences. Rows were loaded and the filter excluded every one
 * of them; nothing was loaded and there is nowhere left to look; nothing was
 * loaded and there is - which is the artwork listing's own quirk, where an
 * empty page with a live token means keep going and claiming the store is empty
 * would be wrong.
 */
function emptyMessage(
  shown: number,
  loaded: number,
  nextPageToken: string,
  filtered: string,
  empty: string,
): string | null {
  if (shown > 0) return null;
  if (loaded > 0) return filtered;
  if (nextPageToken) return null;
  return empty;
}

interface SectionProps {
  loadedCount: number;
  heading: boolean;
  loading: boolean;
  error: string | null;
  nextPageToken: string;
  onLoadMore: () => void;
  onLoad: (entityId: string) => void;
}

function TextCorrections({
  rows,
  loadedCount,
  heading,
  loading,
  error,
  nextPageToken,
  onLoadMore,
  onLoad,
}: SectionProps & { rows: EntityOverride[] }) {
  const message = emptyMessage(
    rows.length,
    loadedCount,
    nextPageToken,
    "No corrections match.",
    "No corrections yet.",
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {heading ? <SectionHeading>Text</SectionHeading> : null}

      {loading && loadedCount === 0 ? <LoadingSpinner /> : null}

      {/* Entity and Changed are FIXED, and identically so on both tables. They
          are the two columns every row is read by and the only two both
          sources have, so a fixed track is what makes the sections scan as one
          list; a shared `fr` would not, because the columns after them differ
          and the free space divides differently. */}
      <DataTable
        label="Text corrections"
        columns="220px 140px minmax(200px, 1.4fr) minmax(130px, 0.8fr) minmax(130px, 0.8fr) minmax(90px, 0.6fr)"
        header={["Entity", "Changed", "Reason", "Edited by", "Edited", ""]}
        headerClassName="text-muted-foreground text-[11px] tracking-wider uppercase"
      >
        {rows.map((override) => {
          const changed = overrideChangeLabels(override);
          const loadable = entityKindForId(override.entityId) !== null;
          return (
            <DataRow
              key={override.entityId}
              className="text-sm"
              onActivate={loadable ? () => onLoad(override.entityId) : undefined}
            >
              {/* Composite, so no caption: an id above the name it was
                  corrected to needs no word to say what it is. */}
              <DataCell>
                <div className="font-mono text-[11px] break-words">{override.entityId}</div>
                {override.displayName ? (
                  <div className="text-muted-foreground break-words">{override.displayName}</div>
                ) : null}
              </DataCell>
              <DataCell label="Changed">
                {changed.length > 0 ? (
                  changed.join(", ")
                ) : (
                  <span className="text-muted-foreground">Not set</span>
                )}
              </DataCell>
              <DataCell label="Reason">
                {override.reason || <span className="text-muted-foreground">Not set</span>}
              </DataCell>
              <DataCell label="Edited by" className="text-muted-foreground">
                {override.updatedBy || "unknown"}
              </DataCell>
              <DataCell label="Edited" className="text-muted-foreground">
                {formatDateTime(override.updatedAt)}
              </DataCell>
              <DataCell>
                <RowLoadButton entityId={override.entityId} onLoad={onLoad} />
              </DataCell>
            </DataRow>
          );
        })}
      </DataTable>

      {error ? <div className="text-muted-foreground p-6 text-center text-sm">{error}</div> : null}
      {!loading && !error && message ? (
        <div className="text-muted-foreground p-6 text-center text-sm">{message}</div>
      ) : null}

      {nextPageToken ? (
        <Button
          variant="outline"
          aria-label="Load more corrections"
          className="w-full md:w-auto md:self-start"
          disabled={loading}
          onClick={onLoadMore}
        >
          Load more
        </Button>
      ) : null}
    </div>
  );
}

function ArtworkChanges({
  rows,
  loadedCount,
  heading,
  role,
  loading,
  error,
  nextPageToken,
  onLoadMore,
  onLoad,
  onRemove,
  removing,
  removeStatus,
  removeError,
}: SectionProps & {
  rows: EntityAssetListing[];
  role?: EntityAssetRole;
  onRemove: (asset: EntityAssetListing) => void;
  removing: boolean;
  removeStatus: string | null;
  removeError: string | null;
}) {
  const message = emptyMessage(
    rows.length,
    loadedCount,
    nextPageToken,
    "No artwork matches.",
    role ? "No artwork of this kind." : "No artwork stored yet.",
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {heading ? <SectionHeading>Artwork</SectionHeading> : null}

      {loading && loadedCount === 0 ? <LoadingSpinner /> : null}

      <DataTable
        label="Artwork"
        columns="220px 140px minmax(100px, 0.6fr) minmax(130px, 0.8fr) minmax(90px, 0.6fr) minmax(130px, 0.8fr)"
        header={["Entity", "Changed", "Size", "Edited", "", ""]}
        headerClassName="text-muted-foreground text-[11px] tracking-wider uppercase"
      >
        {rows.map((asset) => {
          const loadable = entityKindForId(asset.entityId) !== null;
          return (
            <DataRow
              key={asset.objectKey}
              className="text-sm"
              onActivate={loadable ? () => onLoad(asset.entityId) : undefined}
            >
              {/* The id and the image, not the object key. A row says what was
                  changed and whose it is; where the file sits is derivable from
                  both and is not something an operator reads. */}
              <DataCell className="font-mono text-[11px] break-words">{asset.entityId}</DataCell>
              <DataCell label="Changed">{entityAssetRoleSpec(asset.role).label}</DataCell>
              <DataCell label="Size" className="text-muted-foreground">
                {formatBytes(asset.sizeBytes)}
              </DataCell>
              <DataCell label="Edited" className="text-muted-foreground">
                {formatDateTime(asset.updatedAt)}
              </DataCell>
              <DataCell>
                <RowLoadButton entityId={asset.entityId} onLoad={onLoad} />
              </DataCell>
              {/* Its own cell rather than a second button beside Load: the two
                  go different places, and on a phone every cell is its own
                  stacked line. */}
              <DataCell>
                <RowRemoveButton asset={asset} disabled={removing} onRemove={onRemove} />
              </DataCell>
            </DataRow>
          );
        })}
      </DataTable>

      {error ? <div className="text-muted-foreground p-6 text-center text-sm">{error}</div> : null}
      {!loading && !error && message ? (
        <div className="text-muted-foreground p-6 text-center text-sm">{message}</div>
      ) : null}

      {/* The Entity tab's pair, in the same two styles: what happened, then
          what went wrong. */}
      {removeStatus ? (
        <p className="text-muted-foreground m-0 text-xs leading-relaxed">{removeStatus}</p>
      ) : null}
      {removeError ? (
        <p className="text-destructive m-0 text-xs leading-relaxed">{removeError}</p>
      ) : null}

      {nextPageToken ? (
        <Button
          variant="outline"
          aria-label="Load more artwork"
          className="w-full md:w-auto md:self-start"
          disabled={loading}
          onClick={onLoadMore}
        >
          Load more
        </Button>
      ) : null}
    </div>
  );
}
